import {
  getAlphaCatalogueLabel,
  type AlphaCatalogue,
  type VehicleV2ResponseBody,
} from '@online-offer/shared';
import {LocaleDateTimeFormat, NumberFormatter} from 'platform/locale';

import {Nullish, parseDate} from 'shared/web';

import {
  ElectricalConsumptionData,
  getElectricalConsumptionData,
} from '../../../utils/getFuelConsumptionData';
import {getHourTranslation, getMinuteTranslation} from '../../../utils/getTimeTranslation';
import {
  getVehicleConsumptionVariant,
  shouldShowBatteryOwnershipAndBidirectional,
  shouldShowElectricalConsumptionInElectricalParams,
  shouldShowElectricalParameters,
  VehicleConsumptionVariant,
} from '../../../utils/getVehicleConsumptionVariant';

export type ElectricalParametersType = {
  batteryCapacity: string | Nullish;
  batteryWarranty: string | Nullish;
  electricRange: string | Nullish;
  chargingPowerAc: string | Nullish;
  chargingPowerDc: string | Nullish;
  electricEnginePower: string | Nullish;
  chargingTimeAc: string | Nullish;
  chargingTimeDc: string | Nullish;
  batteryVoltage: string | Nullish;
  carbonDioxideEmissionDischarged: string | Nullish;
  co2ClassDischarged: string | Nullish;
};

export type AdditionalElectricalParameters = {
  hasRangeExtender: boolean | Nullish;
  batteryType: string | Nullish;
  chargingConnectorType: string | Nullish;
  numberOfBatteries: number | Nullish;
  batteryOwnershipType: string | Nullish;
  biDirectionalCharging: string | Nullish;
  actualBatteryRange: string | Nullish;
};

export type GetElectricalParameters = {
  electricalData: ElectricalParametersType;
  additionalElectricalData: AdditionalElectricalParameters;
  electricalConsumptionData: ElectricalConsumptionData | Nullish;
  hybridType: string | Nullish;
  consumptionVariant: VehicleConsumptionVariant;
};

export function getElectricalParameters(
  vehicle: VehicleV2ResponseBody | Nullish,
  alphaCatalogue: AlphaCatalogue | Nullish,
  formatDateTime: (dateTimeFormat: LocaleDateTimeFormat, value: Date) => string,
  formatNumber: NumberFormatter
): GetElectricalParameters | null {
  if (!vehicle || !alphaCatalogue) {
    return null;
  }

  const consumptionVariant = getVehicleConsumptionVariant(vehicle);

  if (!shouldShowElectricalParameters(consumptionVariant)) {
    return null;
  }

  const hybridType = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'hybrid_type',
    key: vehicle.hybridType,
    alphaCatalogue,
  });

  const batteryCapacityKWh = vehicle.batteryCapacityWh ? `${vehicle.batteryCapacityWh} kWh` : null;

  const batteryCapacityAh = vehicle.batteryCapacityAh ? `${vehicle.batteryCapacityAh} Ah` : null;

  const batteryCapacity = [batteryCapacityKWh, batteryCapacityAh].filter(Boolean).join(' / ');

  const batteryWarrantyDate = vehicle.batteryWarrantyUntil
    ? `${formatDateTime('dateShort', parseDate(vehicle.batteryWarrantyUntil))}`
    : null;
  const batteryWarrantyKm = vehicle.batteryMileageLimit
    ? `${formatNumber(vehicle.batteryMileageLimit, 0)} km`
    : null;

  const batteryWarranty = [batteryWarrantyKm, batteryWarrantyDate].filter(Boolean).join(' / ');

  const electricRange = vehicle.electricRange ? `${vehicle.electricRange} km` : null;

  const chargingPowerAc = vehicle.chargingAC ? `${vehicle.chargingAC} kW` : null;
  const chargingPowerDc = vehicle.chargingDC ? `${vehicle.chargingDC} kW` : null;
  const electricEnginePower = vehicle.powerElectricEngine
    ? `${vehicle.powerElectricEngine} kW`
    : null;
  const chargingTimeAc = vehicle.chargingTimeAC
    ? `${vehicle.chargingTimeAC} ${getHourTranslation(vehicle.chargingTimeAC)}`
    : null;
  const chargingTimeDc = vehicle.chargingTimeDC
    ? `${vehicle.chargingTimeDC} ${getMinuteTranslation(vehicle.chargingTimeDC)}`
    : null;
  const batteryVoltage = vehicle.batteryVoltage ? `${vehicle.batteryVoltage} V` : null;
  const hasRangeExtender = vehicle.hasRangeExtender ?? null;
  const batteryType = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'battery_type',
    key: vehicle.batteryType,
    alphaCatalogue,
  });
  const chargingConnectorType = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'charging_connector_type',
    key: vehicle.chargingConnectorType,
    alphaCatalogue,
  });
  const numberOfBatteries = vehicle.batteryCount ?? null;

  const showBatteryOwnershipAndBidirectional =
    shouldShowBatteryOwnershipAndBidirectional(consumptionVariant);

  const batteryOwnershipType = showBatteryOwnershipAndBidirectional
    ? getAlphaCatalogueLabel({
        vehicleType: vehicle.type,
        propertyName: 'battery_ownership_type',
        key: vehicle.batteryOwnershipType,
        alphaCatalogue,
      })
    : null;

  const biDirectionalCharging = showBatteryOwnershipAndBidirectional
    ? getAlphaCatalogueLabel({
        vehicleType: vehicle.type,
        propertyName: 'bidirectional_charging',
        key: vehicle.biDirectionalCharging,
        alphaCatalogue,
      })
    : null;

  const actualBatteryRange =
    showBatteryOwnershipAndBidirectional && vehicle.electricRange
      ? `${vehicle.electricRange} km`
      : null;

  const carbonDioxideEmissionDischarged = vehicle.carbonDioxideEmissionDischarged
    ? `${vehicle.carbonDioxideEmissionDischarged} g/km`
    : null;

  const co2ClassDischarged = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'emission_class',
    key: vehicle.carbonDioxideEmissionClassDischarged,
    alphaCatalogue,
  });

  const electricalConsumptionData = shouldShowElectricalConsumptionInElectricalParams(
    consumptionVariant
  )
    ? getElectricalConsumptionData(vehicle, alphaCatalogue)
    : null;

  return {
    electricalData: {
      batteryCapacity,
      batteryWarranty,
      electricRange,
      chargingPowerAc,
      chargingPowerDc,
      electricEnginePower,
      chargingTimeAc,
      chargingTimeDc,
      batteryVoltage,
      carbonDioxideEmissionDischarged,
      co2ClassDischarged,
    },
    additionalElectricalData: {
      hasRangeExtender,
      batteryType,
      chargingConnectorType,
      numberOfBatteries,
      batteryOwnershipType,
      biDirectionalCharging,
      actualBatteryRange,
    },
    electricalConsumptionData,
    hybridType,
    consumptionVariant,
  };
}
